import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable } from 'rxjs';

/**
 * Components that opt into unsaved-changes protection implement this contract.
 * The guard calls `hasUnsavedChanges()`; if it returns true, a confirm dialog
 * gates navigation.
 */
export interface HasUnsavedChanges {
  hasUnsavedChanges: () => boolean;
}

/**
 * Reusable CanDeactivateFn. The component must implement HasUnsavedChanges.
 * When dirty, prompts via ConfirmationService — "Verwerfen" allows navigation,
 * "Bleiben" cancels it.
 *
 * Net-new pattern in this codebase. Currently used on ProductDetailComponent
 * (U8) and reusable for any future complex detail page.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (
  component,
) => {
  if (!component.hasUnsavedChanges()) {
    return true;
  }

  const confirmationService = inject(ConfirmationService);
  const translate = inject(TranslateService);

  return new Observable<boolean>((observer) => {
    confirmationService.confirm({
      header: translate.instant('catalog.unsavedChanges.header'),
      message: translate.instant('catalog.unsavedChanges.message'),
      acceptLabel: translate.instant('catalog.unsavedChanges.discard'),
      rejectLabel: translate.instant('catalog.unsavedChanges.stay'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        observer.next(true);
        observer.complete();
      },
      reject: () => {
        observer.next(false);
        observer.complete();
      },
    });
  });
};
